/**
 * Cookie key for the user's active location preference, namespaced by tenant.
 *
 * Lives in a non-`server-only` module so both server (loader, layout) and
 * client (booking form, native top-bar drawer) can import the same helper.
 * Centralizing the format here means changing it doesn't require chasing
 * down hand-rolled template literals scattered across the app.
 */
export function activeLocationCookieKey(tenantId: string): string {
  return `book:last-location:${tenantId}`;
}

/**
 * In-page event fired whenever a client writes the active-location cookie.
 * The native top-bar chip listens for this so it can re-read its display
 * value without waiting for a navigation or full refresh — important when
 * the user changes location from inside /book (separate React tree from
 * the chip in the app shell).
 *
 * Browser `storage` events would seem like the right tool but they only
 * fire across tabs, not within the same tab where both the writer and the
 * listener live. A custom event keeps it explicit and one-line for any
 * future writer to participate.
 */
export const ACTIVE_LOCATION_CHANGED_EVENT = 'book:active-location-changed';

export interface ActiveLocationChangedDetail {
  tenantId: string;
  slug: string;
}

export function dispatchActiveLocationChanged(
  detail: ActiveLocationChangedDetail,
) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent<ActiveLocationChangedDetail>(
      ACTIVE_LOCATION_CHANGED_EVENT,
      { detail },
    ),
  );
}

/**
 * Shape of a `court_locations (slug, is_active, is_deleted)` embed. Pages
 * that already join the venue for other reasons add these three columns and
 * pass the row through here instead of running a second lookup.
 */
export interface EmbeddedLocationRow {
  slug: string | null;
  is_active: boolean | null;
  is_deleted: boolean | null;
}

/** Slug of an embedded venue, or null when it is archived / soft-deleted —
 *  a stale slug in the cookie would otherwise fall through to "no venue". */
export function liveLocationSlug(
  row: EmbeddedLocationRow | null | undefined,
): string | null {
  if (!row?.slug || row.is_active === false || row.is_deleted === true) {
    return null;
  }
  return row.slug;
}
